import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd'
import { DeleteOutlined, LoginOutlined, PlusOutlined, UserOutlined } from '@ant-design/icons'
import { api } from '../services/api'

const { Text } = Typography
const { Option } = Select

export interface BusinessUserRow {
  id: string
  firstName: string
  lastName: string
  email: string
  phone?: string | null
  role: string
  isActive: boolean
  isVerified: boolean
  lastLoginAt?: string | null
  accessType?: 'direct' | 'multi_business'
}

const ROLE_OPTIONS = [
  'OWNER',
  'ADMIN',
  'SUPER_ADMIN',
  'MANAGER',
  'TECHNICIAN',
  'OFFICE_STAFF',
]

interface BusinessUsersTabProps {
  businessId: string
  businessName?: string
  onUsersChanged?: () => void
}

const BusinessUsersTab: React.FC<BusinessUsersTabProps> = ({
  businessId,
  businessName,
  onUsersChanged,
}) => {
  const [users, setUsers] = useState<BusinessUserRow[]>([])
  const [loading, setLoading] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [impersonatingId, setImpersonatingId] = useState<string | null>(null)
  const [editUser, setEditUser] = useState<BusinessUserRow | null>(null)
  const [savingEdit, setSavingEdit] = useState(false)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [createForm] = Form.useForm()
  const [editForm] = Form.useForm()

  const loadUsers = useCallback(async () => {
    setLoading(true)
    try {
      const res = await api.get(`/businesses/${businessId}/users`)
      setUsers(res.data?.data || [])
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || 'Failed to load users')
    } finally {
      setLoading(false)
    }
  }, [businessId])

  useEffect(() => {
    void loadUsers()
  }, [loadUsers])

  const handleCreate = async (values: {
    email: string
    firstName: string
    lastName: string
    role: string
    password?: string
    phone?: string
    isVerified?: boolean
  }) => {
    setCreating(true)
    try {
      const res = await api.post(`/businesses/${businessId}/users`, values)
      setCreateOpen(false)
      createForm.resetFields()
      await loadUsers()
      onUsersChanged?.()
      if (res.data?.temporaryPassword) {
        Modal.success({
          title: 'User created',
          content: (
            <div>
              <p>
                Share this temporary password with {values.firstName} {values.lastName}:
              </p>
              <Input.TextArea
                readOnly
                value={res.data.temporaryPassword}
                autoSize
                style={{ fontFamily: 'monospace' }}
              />
            </div>
          ),
        })
      } else {
        message.success('User created successfully')
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || 'Failed to create user')
    } finally {
      setCreating(false)
    }
  }

  const handleEdit = async (values: {
    firstName: string
    lastName: string
    role: string
    isActive: boolean
    isVerified: boolean
    password?: string
  }) => {
    if (!editUser) return
    setSavingEdit(true)
    try {
      await api.put(`/businesses/${businessId}/users/${editUser.id}`, values)
      message.success('User updated')
      setEditUser(null)
      editForm.resetFields()
      await loadUsers()
      onUsersChanged?.()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || 'Failed to update user')
    } finally {
      setSavingEdit(false)
    }
  }

  const handleImpersonate = async (userId: string) => {
    setImpersonatingId(userId)
    try {
      const res = await api.post(`/businesses/${businessId}/impersonate`, { userId })
      if (res.data?.magicLink) {
        window.open(res.data.magicLink, '_blank')
        message.success('Opened business account in a new tab')
      }
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || 'Failed to impersonate user')
    } finally {
      setImpersonatingId(null)
    }
  }

  const handleDelete = async (row: BusinessUserRow) => {
    setDeletingId(row.id)
    try {
      const res = await api.delete(`/businesses/${businessId}/users/${row.id}`)
      message.success(res.data?.message || 'User deleted')
      if (editUser?.id === row.id) {
        setEditUser(null)
        editForm.resetFields()
      }
      await loadUsers()
      onUsersChanged?.()
    } catch (error: unknown) {
      const err = error as { response?: { data?: { message?: string } } }
      message.error(err.response?.data?.message || 'Failed to delete user')
    } finally {
      setDeletingId(null)
    }
  }

  const deleteConfirmTitle = (row: BusinessUserRow) =>
    row.accessType === 'multi_business'
      ? `Remove ${row.firstName} ${row.lastName} from this company?`
      : `Delete ${row.firstName} ${row.lastName}?`

  const deleteConfirmDescription = (row: BusinessUserRow) =>
    row.accessType === 'multi_business'
      ? 'They will lose access to this company. Their account on other companies is unchanged.'
      : 'They will be removed from this company and can no longer sign in. This cannot be undone from admin.'

  const adminUsers = users.filter((u) => u.isActive && ['OWNER', 'ADMIN', 'SUPER_ADMIN'].includes(u.role))

  return (
    <div>
      {users.length === 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No users for this business"
          description="Add an Owner or Admin user below so you can use Login as Business and the customer can access the app."
        />
      )}

      {users.length > 0 && adminUsers.length === 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="No Owner/Admin users"
          description="Impersonation requires an active Owner, Admin, or Super Admin. You can still log in as another active user, or promote a user to Owner below."
        />
      )}

      <Card
        title={
          <Space>
            <UserOutlined />
            <span>Business users{businessName ? ` — ${businessName}` : ''}</span>
          </Space>
        }
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
            Add user
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={users}
          pagination={false}
          columns={[
            {
              title: 'Name',
              key: 'name',
              render: (_, row) => (
                <div>
                  <div style={{ fontWeight: 600 }}>
                    {row.firstName} {row.lastName}
                  </div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {row.email}
                  </Text>
                </div>
              ),
            },
            {
              title: 'Role',
              dataIndex: 'role',
              key: 'role',
              render: (role: string) => <Tag>{role}</Tag>,
            },
            {
              title: 'Status',
              key: 'status',
              render: (_, row) => (
                <Space size={4} wrap>
                  <Tag color={row.isActive ? 'green' : 'default'}>{row.isActive ? 'Active' : 'Inactive'}</Tag>
                  {row.isVerified ? <Tag color="blue">Verified</Tag> : <Tag>Unverified</Tag>}
                  {row.accessType === 'multi_business' && <Tag>Multi-business</Tag>}
                </Space>
              ),
            },
            {
              title: 'Last login',
              dataIndex: 'lastLoginAt',
              key: 'lastLoginAt',
              render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
            },
            {
              title: 'Actions',
              key: 'actions',
              render: (_, row) => (
                <Space wrap>
                  <Button
                    size="small"
                    icon={<LoginOutlined />}
                    loading={impersonatingId === row.id}
                    disabled={!row.isActive}
                    onClick={() => handleImpersonate(row.id)}
                  >
                    Login as
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setEditUser(row)
                      editForm.setFieldsValue({
                        firstName: row.firstName,
                        lastName: row.lastName,
                        role: row.role,
                        isActive: row.isActive,
                        isVerified: row.isVerified,
                      })
                    }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title={deleteConfirmTitle(row)}
                    description={deleteConfirmDescription(row)}
                    okText="Delete"
                    okButtonProps={{ danger: true, loading: deletingId === row.id }}
                    onConfirm={() => handleDelete(row)}
                  >
                    <Button size="small" danger icon={<DeleteOutlined />} loading={deletingId === row.id}>
                      Delete
                    </Button>
                  </Popconfirm>
                </Space>
              ),
            },
          ]}
        />
      </Card>

      <Modal
        title="Add business user"
        open={createOpen}
        onCancel={() => {
          setCreateOpen(false)
          createForm.resetFields()
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={createForm}
          layout="vertical"
          initialValues={{ role: 'OWNER', isVerified: true }}
          onFinish={handleCreate}
        >
          <Form.Item name="firstName" label="First name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label="Last name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="email" label="Email" rules={[{ required: true, type: 'email' }]}>
            <Input />
          </Form.Item>
          <Form.Item name="phone" label="Phone">
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select>
              {ROLE_OPTIONS.map((r) => (
                <Option key={r} value={r}>
                  {r}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="password" label="Password (optional)" extra="Leave blank to auto-generate a temporary password">
            <Input.Password />
          </Form.Item>
          <Form.Item name="isVerified" label="Email verified" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={creating}>
                Create user
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={editUser ? `Edit ${editUser.firstName} ${editUser.lastName}` : 'Edit user'}
        open={Boolean(editUser)}
        onCancel={() => {
          setEditUser(null)
          editForm.resetFields()
        }}
        footer={null}
        destroyOnClose
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="firstName" label="First name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="lastName" label="Last name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="role" label="Role" rules={[{ required: true }]}>
            <Select>
              {ROLE_OPTIONS.map((r) => (
                <Option key={r} value={r}>
                  {r}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="password" label="New password (optional)">
            <Input.Password />
          </Form.Item>
          <Form.Item name="isActive" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="isVerified" label="Verified" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button onClick={() => setEditUser(null)}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={savingEdit}>
                Save
              </Button>
              {editUser && (
                <Popconfirm
                  title={deleteConfirmTitle(editUser)}
                  description={deleteConfirmDescription(editUser)}
                  okText="Delete"
                  okButtonProps={{ danger: true, loading: deletingId === editUser.id }}
                  onConfirm={() => handleDelete(editUser)}
                >
                  <Button danger icon={<DeleteOutlined />} loading={deletingId === editUser.id}>
                    Delete
                  </Button>
                </Popconfirm>
              )}
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  )
}

export default BusinessUsersTab
